import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

interface EditableCellProps {
  value: number;
  onSave: (newValue: number) => Promise<void>;
  formatValue?: (value: number) => string;
  validateValue?: (value: number) => boolean;
  disabled?: boolean;
  integerOnly?: boolean;
}

const EditableCell: React.FC<EditableCellProps> = ({
  value,
  onSave, 
  formatValue = (val: number) =>
    `₹ ${Number(val || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`,
  validateValue = (val: number) => val >= 0,
  disabled = false,
  integerOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setEditValue(value.toString());
  }, [value]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    } else if (integerOnly && (e.key === "." || e.key === ",")) {
      e.preventDefault();
    }
  };

  const handleSave = async () => {
    const numericValue = integerOnly 
      ? parseInt(editValue, 10) 
      : parseFloat(editValue);

    if (isNaN(numericValue) || !validateValue(numericValue)) {
      toast.error(integerOnly 
        ? "Please enter a valid positive integer" 
        : "Please enter a valid positive number");
      setEditValue(value.toString());
      setIsEditing(false);
      return;
    }

    if (numericValue === value) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    try {
      await onSave(numericValue);
      setIsEditing(false);
    } catch (error: any) {
      console.error("Failed to save:", error);
      // Don't show duplicate error messages - the onSave function already handles error display
      setEditValue(value.toString());
      setIsEditing(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value.toString());
    setIsEditing(false);
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <div className="d-flex align-items-center">
        <input
          type="number"
          value={editValue}
          onChange={(e) => {
            const val = e.target.value;
            if (integerOnly) {
              // Only allow digits, no decimals
              const intVal = val.replace(/[^0-9]/g, "");
              setEditValue(intVal);
            } else {
              setEditValue(val);
            }
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="form-control form-control-sm"
          style={{ width: "100px" }}
          autoFocus
          disabled={isLoading}
          step={integerOnly ? "1" : "0.01"}
          min="0"
        />
        {isLoading && (
          <div className="spinner-border spinner-border-sm ms-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      style={{
        cursor: disabled ? "default" : "pointer",
        padding: "4px 8px",
        borderRadius: "4px",
        transition: "background-color 0.2s",
        minHeight: "32px",
        display: "flex",
        alignItems: "center",
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = "#f8f9fa";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      {formatValue(value)}
    </div>
  );
};

export default EditableCell;
